import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import type { UserProfile } from '../hooks/useProfile';
import { useAuthStore } from './auth.store';

interface ProfileState {
  profile: UserProfile | null;
  loading: boolean;
  error: string | null;
  setProfile: (profile: UserProfile | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  fetchProfile: (userId: string) => Promise<void>;
}

const useProfileStore = create<ProfileState>((set) => ({
  profile: null,
  loading: false,
  error: null,
  setProfile: (profile) => set({ profile }),
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),
  fetchProfile: async (userId: string) => {
    // console.log('[ProfileStore] fetchProfile called for user:', userId); // <-- Add this log
    if (!userId) {
      // console.log('[ProfileStore] No userId provided, returning.'); // <-- Add this log
      return;
    }
    set({ loading: true, error: null });
    try {
      const { data, error: fetchError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      if (fetchError) {
        set({ error: fetchError.message, profile: null });
      } else {
        set({ profile: (data as UserProfile) || null });
      }
    } catch (err: any) {
      set({ error: err.message || 'Unknown error', profile: null });
    } finally {
      set({ loading: false });
    }
  },
}));

useAuthStore.subscribe((state, prevState) => {
  const newUser = state.user;
  const oldUser = prevState.user;
  const authLoading = state.loading;
  
  // Only proceed if auth is not loading
  if (authLoading) return;
  
  // If we have a new user ID and it's different from the old one, fetch the profile.
  if (newUser?.id && newUser.id !== oldUser?.id) {
    // console.log('[ProfileStore] Auth user detected, fetching profile...');
    useProfileStore.getState().fetchProfile(newUser.id);
  }

  // If the user logs out, clear the profile.
  if (!newUser && oldUser) {
    useProfileStore.getState().setProfile(null);
  }
});


export { useProfileStore };
